import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { IconClients, IconClose, IconDelete, IconEdit, IconEmail, IconPhone } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import { isDemoMode, redirectToSignup } from '../lib/demoMode';
import EmptyState from '../components/EmptyState';

export default function Clients() {
  const { theme } = useTheme();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ name:'', contact_name:'', email:'', phone:'', memo:'' });

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) { setLoading(false); return; }

      // 前月の開始・終了日を計算
      const now = new Date();
      const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      const prevMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('clients')
        .select('*, projects(id, amount), invoices(id, amount, status, issued_date)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!error) {
        // 前月入金済みを各クライアントに付加
        const enriched = (data || []).map(c => ({
          ...c,
          _prevMonthPaid: (c.invoices || [])
            .filter(i => {
              const d = i.issued_date || '';
              return i.status === '入金済み' && d >= prevMonthStart && d <= prevMonthEnd;
            })
            .reduce((s, i) => s + (i.amount || 0), 0),
        }));
        setClients(enriched);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const openNew = () => {
    setEditClient(null);
    setForm({ name:'', contact_name:'', email:'', phone:'', memo:'' });
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditClient(c);
    setForm({ name:c.name||'', contact_name:c.contact_name||'', email:c.email||'', phone:c.phone||'', memo:c.memo||'' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    if (isDemoMode()) { redirectToSignup(); return; }
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      const payload = { name:form.name, contact_name:form.contact_name, email:form.email, phone:form.phone, memo:form.memo, user_id:user.id };
      if (editClient) {
        await supabase.from('clients').update(payload).eq('id', editClient.id);
      } else {
        await supabase.from('clients').insert([payload]);
      }
      setShowModal(false);
      fetchClients();
    } catch(e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (isDemoMode()) { redirectToSignup(); return; }
    if (!window.confirm('このクライアントを削除しますか？\n関連する案件・請求書の紐付けは解除されます。')) return;
    await supabase.from('clients').delete().eq('id', id);
    fetchClients();
  };

  // 前月ラベル
  const now = new Date();
  const prevMonthLabel = `${now.getMonth() === 0 ? 12 : now.getMonth()}月`;

  const s = {
    card: { background:theme.bgCard, border:`1px solid ${theme.border}`, boxShadow:theme.shadow, borderRadius:14, overflow:'hidden' },
    label: { fontSize:12, color:theme.textSecondary, marginBottom:6, display:'block' },
    input: { width:'100%', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:10, padding:'10px 14px', fontSize:14, color:theme.textPrimary, outline:'none', boxSizing:'border-box' },
  };

  return (
    <div style={{ padding:'20px 16px', maxWidth:680, margin:'0 auto' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:theme.textPrimary, margin:0 }}><IconClients size={18}/> クライアント管理</h1>
          <p style={{ fontSize:13, color:theme.textMuted, margin:'4px 0 0' }}>{clients.length}社</p>
        </div>
        <button onClick={openNew} style={{ background:theme.primary, color:theme.primaryText, border:'none', borderRadius:10, padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          ＋ 新規登録
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:theme.textMuted }}>読み込み中...</div>
      ) : clients.length === 0 ? (
        <EmptyState preset="clients" onAction={openNew} />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {clients.map(c => {
            const projectCount = c.projects?.length || 0;
            const invoices = c.invoices || [];
            const totalAmount = invoices.reduce((s, i) => s + (i.amount || 0), 0);
            const paidAmount = invoices.filter(i => i.status === '入金済み').reduce((s, i) => s + (i.amount || 0), 0);
            // 平均単価（案件のamountから計算）
            const projectTotal = (c.projects || []).reduce((s, p) => s + (p.amount || 0), 0);
            const avgAmount = projectCount > 0 ? Math.floor(projectTotal / projectCount) : 0;
            // 前月入金済み
            const prevMonthPaid = c._prevMonthPaid || 0;

            return (
              <div key={c.id} style={{ ...s.card, borderLeft:`3px solid ${theme.accent}` }}>
                <div style={{ padding:'16px' }}>

                  {/* ── ヘッダー：アバター + 名前 + 連絡先ボタン ── */}
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
                    <div style={{ width:44, height:44, borderRadius:'50%', background:`linear-gradient(135deg, ${theme.accent}, ${theme.accentSub})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, fontWeight:700, color:'#fff', flexShrink:0 }}>
                      {c.name.charAt(0)}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:16, fontWeight:700, color:theme.textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</div>
                      {c.contact_name && <div style={{ fontSize:12, color:theme.textSecondary, marginTop:2 }}>担当：{c.contact_name}</div>}
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      {c.email && (
                        <a href={`mailto:${c.email}`} title={c.email}
                          style={{ width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center', background:theme.accentBg, border:`1px solid ${theme.accent}`, borderRadius:8, color:theme.accent, textDecoration:'none' }}>
                          <IconEmail size={15}/>
                        </a>
                      )}
                      {c.phone && (
                        <a href={`tel:${c.phone}`} title={c.phone}
                          style={{ width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center', background:theme.bgInput, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.textMuted, textDecoration:'none' }}>
                          <IconPhone size={15}/>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* ── KPIカード（案件数・平均単価・前月入金済み） ── */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
                    <div style={{ background:theme.bgInput, borderRadius:8, padding:'10px', textAlign:'center' }}>
                      <div style={{ fontSize:20, fontWeight:700, color:theme.accent }}>{projectCount}</div>
                      <div style={{ fontSize:10, color:theme.textMuted, marginTop:2 }}>案件数</div>
                    </div>
                    <div style={{ background:theme.bgInput, borderRadius:8, padding:'10px', textAlign:'center' }}>
                      <div style={{ fontSize:12, fontWeight:700, color:theme.textPrimary }}>
                        {avgAmount > 0 ? '¥' + Math.floor(avgAmount / 10000) + '万' : '-'}
                      </div>
                      <div style={{ fontSize:10, color:theme.textMuted, marginTop:2 }}>平均単価</div>
                    </div>
                    <div style={{ background:theme.bgInput, borderRadius:8, padding:'10px', textAlign:'center' }}>
                      <div style={{ fontSize:12, fontWeight:700, color:theme.colorGreen2 }}>
                        {prevMonthPaid > 0 ? '¥' + Math.floor(prevMonthPaid / 10000) + '万' : '-'}
                      </div>
                      <div style={{ fontSize:10, color:theme.textMuted, marginTop:2 }}>{prevMonthLabel}入金済</div>
                    </div>
                  </div>

                  {/* ── 入金率バー ── */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ fontSize:10, color:theme.textMuted }}>入金率</span>
                      <span style={{ fontSize:10, fontWeight:600, color:theme.colorGreen2 }}>
                        {totalAmount > 0 ? Math.round(paidAmount/totalAmount*100) : 0}%
                      </span>
                    </div>
                    <div style={{ height:4, background:theme.border, borderRadius:4 }}>
                      <div style={{ height:'100%', width:`${totalAmount > 0 ? Math.round(paidAmount/totalAmount*100) : 0}%`, background:theme.colorGreen2, borderRadius:4, transition:'width 0.4s' }}/>
                    </div>
                  </div>

                  {c.memo && <p style={{ fontSize:12, color:theme.textMuted, margin:'0 0 12px', lineHeight:1.5, padding:'8px 10px', background:theme.bgInput, borderRadius:8 }}>{c.memo}</p>}

                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => openEdit(c)} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, background:theme.accentBg, color:theme.accent, border:`1px solid ${theme.accent}`, borderRadius:10, padding:'11px', fontSize:13, fontWeight:600, cursor:'pointer' }}><IconEdit size={14}/> 編集</button>
                    <button onClick={() => handleDelete(c.id)} style={{ display:'flex', alignItems:'center', justifyContent:'center', background:theme.dangerBg, color:theme.dangerText, border:'none', borderRadius:10, padding:'11px 16px', fontSize:13, cursor:'pointer' }}><IconDelete size={16}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div onClick={e => { if(e.target===e.currentTarget) setShowModal(false); }}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:50, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div style={{ background:theme.bgCard, width:'100%', maxWidth:520, borderRadius:'20px 20px 0 0', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ position:'sticky', top:0, background:theme.bgCard, borderBottom:'1px solid #334155', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'20px 20px 0 0' }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:theme.textPrimary, margin:0 }}>{editClient ? 'クライアントを編集' : '新規クライアント登録'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', color:theme.textMuted, fontSize:22, cursor:'pointer' }}><IconClose size={18}/></button>
            </div>
            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { label:'会社名 *', key:'name', type:'text', placeholder:'例：株式会社〇〇' },
                { label:'担当者名', key:'contact_name', type:'text', placeholder:'例：田中 様' },
                { label:'メールアドレス', key:'email', type:'email', placeholder:'example@company.com' },
                { label:'電話番号', key:'phone', type:'tel', placeholder:'090-0000-0000' },
              ].map(field => (
                <div key={field.key}>
                  <label style={s.label}>{field.label}</label>
                  <input type={field.type} placeholder={field.placeholder} value={form[field.key]}
                    onChange={e => setForm({...form, [field.key]:e.target.value})} style={s.input} />
                </div>
              ))}
              <div>
                <label style={s.label}>メモ</label>
                <textarea rows={2} placeholder="備考..." value={form.memo}
                  onChange={e => setForm({...form, memo:e.target.value})}
                  style={{ ...s.input, resize:'vertical' }} />
              </div>
              <div style={{ display:'flex', gap:10, paddingBottom:8 }}>
                <button onClick={() => setShowModal(false)} style={{ flex:1, background:theme.badgeBg1, color:theme.textSecondary, border:'none', borderRadius:10, padding:'12px', fontSize:14, cursor:'pointer' }}>キャンセル</button>
                <button onClick={handleSave} disabled={!form.name.trim()}
                  style={{ flex:1, background:form.name.trim()?theme.primary:theme.bgHover, color:form.name.trim()?theme.primaryText:theme.textWeak, border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:600, cursor:form.name.trim()?'pointer':'default' }}>
                  {editClient ? '更新する' : '登録する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
